import { m } from 'motion/react';
import { useNavigate, useRouter } from '@tanstack/react-router';

import config from '@/config';
import { AuthCognito } from '@shared/config';
import { AUTH_KEY, IS_SUBSCRIBED_KEY } from '@shared/auth/authentication.hooks';
import { MotionArticleProps } from '@/lib/article-renderer/article';

import styles from '@/lib/article-renderer/core/article.module.css';
import { useT } from '@shared/i18n';
import { track } from '@shared/events';

/**
 * The article renderer (src/lib/article-renderer) renders this in place of a
 * gated article's components (`components === null`) via its `renderGated`
 * prop -- this is the host app's concrete Cognito-aware implementation, kept
 * out of the library itself since it reaches into this app's `config`,
 * localStorage auth keys and router (improvements/11-article-renderer-
 * library-extraction.md's "renderGated" injection point). Moved verbatim out
 * of virtual-article.tsx's old `SubscriptionRequired` -- same three screens
 * (sign in / subscribe / upgrade), same markup, same behavior.
 */
export function SubscriptionGate(props: Readonly<MotionArticleProps>) {
	const t = useT();
	const navigate = useNavigate();
	const router = useRouter();
	const basename = `${router.basepath.replace(/\/$/, '')}/callback`;

	const { id, ArticleID, index, className, classNames } = props;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	if (!isAuthenticated) {
		return (
			<m.article
				id={`article-${id}`}
				key={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>{t('gate.authRequired')}</h1>
							<h2>{t('gate.signInToRead')}</h2>
							<button
								onClick={() => {
									track('login_start', {
										location: window.location.pathname,
									});
									location.href = redirectUrl;
								}}
							>
								{' '}
								{t('common.signIn')}
							</button>
						</section>
					</div>
				</div>
			</m.article>
		);
	}

	if (!isSubscribed) {
		return (
			<m.article
				id={`article-${id}`}
				key={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>{t('gate.subscriptionRequired')}</h1>
							<h2>
								{t('gate.needSubscription')}
							</h2>
							<button
								type="button"
								onClick={() => {
									track('subscribe_click', {
										location: window.location.pathname,
									});
									navigate({
										to: '/subscriptions',
									});
								}}
							>
								{' '}
								{t('gate.purchase')}
							</button>
						</section>
					</div>
				</div>
			</m.article>
		);
	}

	return (
		<m.article
			id={`article-${id}`}
			data-article-id={ArticleID}
			data-index={index}
			className={className ?? ''}
		>
			<div className={classNames.join(' ')}>
				<div className={styles['subscription-required']}>
					<section>
						<h1>{t('gate.upgradeRequired')}</h1>
						<h2>
							{t('gate.upgradeToRead')}
						</h2>
						<button
							onClick={() => {
								navigate({
									to: '/subscriptions',
								});
							}}
						>
							{' '}
							{t('gate.upgrade')}
						</button>
					</section>
				</div>
			</div>
		</m.article>
	);
}

export default SubscriptionGate;
